import { supabase } from "../supabase/SupabaseClient";
import {
  OPCIONES_DE_TEMA,
  temaGuardado,
  vidrioGuardado,
  vidrioValido,
} from "../components/layout/temasUat";

/* Las preferencias, del lado del servidor.
 *
 * Viven en la base para que sigan a la persona y no al navegador: el mismo
 * usuario en el celular y en la computadora tenía dos configuraciones distintas
 * sin haber pedido ninguna, y cambiar de máquina era empezar de cero.
 *
 * El fondo va aparte, en un bucket privado, porque es una foto y no un ajuste.
 * En la fila queda sólo la ruta. Meter la imagen como data URI en el jsonb
 * serían varios MB traídos en cada carga de la app.
 *
 * Nada de esto tira error hacia arriba. Si la red falla, la app tiene que
 * arrancar igual con lo que haya guardado en el navegador: quedarse sin entrar
 * porque no se pudo leer un color de acento sería absurdo.
 */

const BUCKET = "fondos";

// Una semana. Se pide uno nuevo en cada carga, así que no hace falta que dure
// más — y cuanto menos dure, menos tiempo sirve un enlace que se escapó.
const VIGENCIA = 60 * 60 * 24 * 7;

/* `getSession()` Y NO `getUser()`, y la diferencia no es de estilo.
 *
 * `getUser()` sale a la red SIEMPRE: es un `GET /auth/v1/user` para averiguar
 * un mail que el navegador ya tiene guardado. `getSession()` lo lee de local y
 * no pide nada.
 *
 * Eso solo sería un pedido de más. Lo que lo volvió grave es que cada
 * `getUser()` sobre un token por vencer dispara un refresh, el refresh emite
 * `TOKEN_REFRESHED`, y del otro lado había un efecto que al oírlo volvía a
 * llamar acá. En los logs de auth eso son **32 `/user` y 32 refresh en 61
 * segundos**, hasta que Supabase contesta 429 y la sesión se cae sola.
 */
async function quienSoy() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user || null;
}

/* Devuelve { ajustes, fondoRuta, fondo } o null si no hay nada guardado o no se
 * pudo leer. `fondo` ya viene como enlace usable. */
export async function traerPrefs() {
  try {
    const yo = await quienSoy();
    if (!yo?.email) return null;

    const { data, error } = await supabase
      .from("preferencias")
      .select("ajustes, fondo_ruta")
      .eq("mail", yo.email.toLowerCase())
      .maybeSingle();
    if (error || !data) return null;

    let fondo = null;
    if (data.fondo_ruta) {
      const { data: firmado } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(data.fondo_ruta, VIGENCIA);
      fondo = firmado?.signedUrl || null;
    }

    return { ...(data.ajustes || {}), fondoRuta: data.fondo_ruta, fondo };
  } catch (e) {
    console.warn("[preferencias] no se pudieron leer:", e);
    return null;
  }
}

/* Guarda todo menos la imagen. `fondo` se saca a propósito: es el enlace
 * firmado, que vence — guardarlo dejaría una dirección muerta en la fila. */
export async function guardarPrefs(prefs) {
  try {
    const yo = await quienSoy();
    if (!yo?.email) return "No hay sesión";

    const { fondo, fondoRuta, ...resto } = prefs;
    // El tema del hub viaja con los ajustes (ver `guardarTemaHub`). Esto
    // pisa la fila entera, así que va el que está puesto ahora: si no, cambiar
    // el color de acento borraría el tema de la cuenta.
    const ajustes = { ...resto, temaHub: temaGuardado(), vidrio: vidrioGuardado() };
    const { error } = await supabase.from("preferencias").upsert(
      {
        mail: yo.email.toLowerCase(),
        ajustes,
        fondo_ruta: fondoRuta || null,
        actualizado_en: new Date().toISOString(),
      },
      { onConflict: "mail" }
    );
    return error ? error.message : null;
  } catch (e) {
    return e.message || "No se pudo guardar";
  }
}

/* Sube la imagen y devuelve { ruta, url }. La carpeta es el id de la cuenta:
 * la política del bucket sólo deja escribir ahí, así que nadie puede pisar el
 * fondo de otro.
 *
 * El nombre lleva la fecha porque el enlace firmado del anterior sigue vivo un
 * rato; con un nombre fijo, quien acaba de cambiar la foto seguiría viendo la
 * vieja hasta que venza. */
export async function subirFondo(archivo) {
  const yo = await quienSoy();
  if (!yo?.id) throw new Error("No hay sesión");

  const ext = (archivo.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
  const ruta = `${yo.id}/fondo-${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(ruta, archivo, { contentType: archivo.type, upsert: true });
  if (error) throw error;

  const { data } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(ruta, VIGENCIA);

  return { ruta, url: data?.signedUrl || null };
}

/* Borrar la anterior no es opcional por prolijidad: sin esto cada cambio de
 * fondo deja una foto para siempre en el bucket. */
export async function borrarFondo(ruta) {
  if (!ruta) return;
  try {
    await supabase.storage.from(BUCKET).remove([ruta]);
  } catch (e) {
    console.warn("[preferencias] no se pudo borrar el fondo viejo:", e);
  }
}

/* EL TEMA DEL HUB SIGUE A LA CUENTA.
 *
 * Vivía sólo en el navegador: quien entraba desde el celular arrancaba en
 * Estándar aunque en la computadora tuviera Arcade, y no había forma de
 * ponerle un tema a otra persona. Igna (08/10): «¿podemos asignarle nosotros
 * un tema a alguien?… sugerido siempre» —la sorpresa era que Aldo abriera el
 * hub y fuera de River—.
 *
 * Va adentro de `ajustes`, con el resto de la apariencia: `temaHub`. Es
 * SUGERIDO: lo que hay en la cuenta se pone al entrar, y cuando la persona
 * elige otro se guarda ése. No hay candado.
 *
 * `temaHubDe` saca el tema de lo que devolvió `traerPrefs`, y devuelve null
 * si no hay ninguno o si es uno que ya no existe. */
export function temaHubDe(prefs) {
  const t = prefs?.temaHub;
  return OPCIONES_DE_TEMA.some(([id]) => id === t) ? t : null;
}

/* Guarda un ajuste del hub (el tema, la opacidad) sin tocar lo demás: lee la
 * fila y la vuelve a escribir con el tema cambiado. Devuelve el problema, o
 * null si salió bien; quien llama no espera nada —el tema ya está puesto. */
export async function guardarAjustesHub(parche) {
  try {
    const yo = await quienSoy();
    if (!yo?.email) return "No hay sesión";
    const mail = yo.email.toLowerCase();
    const { data } = await supabase
      .from("preferencias")
      .select("ajustes, fondo_ruta")
      .eq("mail", mail)
      .maybeSingle();
    const { error } = await supabase.from("preferencias").upsert(
      {
        mail,
        ajustes: { ...(data?.ajustes || {}), ...parche },
        fondo_ruta: data?.fondo_ruta || null,
        actualizado_en: new Date().toISOString(),
      },
      { onConflict: "mail" }
    );
    return error ? error.message : null;
  } catch (e) {
    return e.message || "No se pudo guardar";
  }
}

export const guardarTemaHub = (tema) => guardarAjustesHub({ temaHub: tema });

/* La opacidad de las tarjetas viaja igual que el tema: en `ajustes.vidrio`. */
export const guardarVidrioHub = (vidrio) => guardarAjustesHub({ vidrio });
export const vidrioDe = (prefs) => vidrioValido(prefs?.vidrio ?? null);
